import { useEffect, useRef, useState } from 'react';

import { useAudioContextValue } from '@/audio/AudioProvider';
import { gameConfigQueryOptions } from '@/features/sequence-recall/api/gameConfig.query';
import { appConfig } from '@app/config/appConfig';
import { queryClient } from '@app/providers/QueryProvider';

const GAME_AUDIO_SCENE = 'sequence-recall';

export type AuthGateLoadingPhase = 'dev-session' | 'config' | 'audio' | 'assets';

async function prefetchGameConfig(): Promise<void> {
  await queryClient.ensureQueryData(gameConfigQueryOptions(appConfig.socket.stageId));
}

const AUDIO_READY_TIMEOUT_MS = 2_000;
const AUDIO_PRELOAD_TIMEOUT_MS = 3_000;

function timeout(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function waitForAudioReady(isReady: () => boolean): Promise<void> {
  if (isReady()) return;
  await Promise.race([
    new Promise<void>((resolve) => {
      const intervalId = window.setInterval(() => {
        if (isReady()) {
          window.clearInterval(intervalId);
          resolve();
        }
      }, 16);
    }),
    timeout(AUDIO_READY_TIMEOUT_MS),
  ]);
}

async function prefetchGameAudio(
  getAudio: () => ReturnType<typeof useAudioContextValue>,
): Promise<void> {
  const audio = getAudio();
  await waitForAudioReady(() => audio.ready);
  await Promise.race([
    audio.preloadScene(GAME_AUDIO_SCENE),
    timeout(AUDIO_PRELOAD_TIMEOUT_MS),
  ]);
}

export function useAuthGate() {
  const audio = useAudioContextValue();
  const audioRef = useRef(audio);
  audioRef.current = audio;
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<AuthGateLoadingPhase>('config');

  useEffect(() => {
    if (isReady) return;

    void (async () => {
      try {
        setPhase('config');
        await prefetchGameConfig();

        setPhase('audio');
        await prefetchGameAudio(() => audioRef.current);

        setIsReady(true);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Authentication failed');
      }
    })();
  }, [isReady]);

  return { isReady, error, phase };
}
